import { memo, useEffect, useReducer, useCallback } from 'react';
import { Badge, Markdown, Modal, Text, useTheme } from '@Sage/hoi-poi-ui';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { logEvent } from 'utils/tracking';

import './styles.scss';

const initialState = {
    detailLog: null,
    log: null,
    open: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'init':
            return {
                ...state,
                open: true,
                detailLog: action.detailLog,
                log: action.log,
            };
        case 'load':
            return {
                ...state,
                canRender: true,
            };
        case 'close':
            return {
                ...state,
                open: false,
            };
        default:
            throw new Error('No action provided');
    }
}

const DetailLogModal = memo(({ onRef }) => {
    const theme = useTheme();
    const [state, dispatch] = useReducer(reducer, initialState);
    const showSupportChat = Boolean(window.Intercom);

    useEffect(() => {
        onRef &&
            onRef({
                open(log, detailLog) {
                    dispatch({
                        type: 'init',
                        detailLog: detailLog,
                        log: log,
                    });
                },
            });
    }, [onRef]);

    const onRequestClose = useCallback(() => {
        dispatch({ type: 'close' });
    }, []);

    const openIntercom = useCallback(() => {
        window.Intercom('showNewMessage');
    }, []);

    const onConfirm = useCallback(() => {
        const blob = new Blob([JSON.stringify(state.detailLog?.info, null, 2)], {
            type: 'application/json',
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `automation-log-${state.log.id}.json`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        logEvent({
            event: 'settings',
            submodule: 'automationsHistory',
            functionality: 'downloadTechnicalReport',
        });
    }, [state.detailLog, state.log?.id]);

    if (!state.open) return null;

    return (
        <Modal
            className="fm-automation__detail-log-modal"
            overlayClassName="fm-automation__detail-log-modal__overlay"
            title={getLiteralWithParameters('label_result_details_title', [
                state.log?.id,
                state.log?.automation,
            ])}
            preComponent={
                <div className="fm-automation__detail-log-modal__result">
                    <Text color={theme.colors.textLight.secondary}>
                        {getLiteral('cfm_label_result')}:
                    </Text>
                    <Badge type={state.log?.result?.type}>{state.log?.result?.text}</Badge>
                </div>
            }
            size="huge"
            useContentStaticHeight
            onRequestClose={onRequestClose}
            cancelText={getLiteral('action_cancel')}
            onCancel={onRequestClose}
            onConfirm={onConfirm}
            onMiddleButton={showSupportChat ? openIntercom : undefined}
            middleButtonText={showSupportChat ? getLiteral('action_support_chat') : null}
            confirmText={getLiteral('action_automations_download_technical_report')}
            isOpen={state.open}
        >
            <div className="fm-automation__detail-log-modal__content">
                <Markdown content={state.detailLog?.markdown?.replace(/\n/g, ' \n')} />
            </div>{' '}
        </Modal>
    );
});

export default DetailLogModal;
